import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import LanguageSwitcher from "../components/LanguageSwitcher";

function Home() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 border-b border-orange-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img
                src="/logoBlack.png"
                alt="Autoshkolla Burimi"
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              {!loading && (
                <>
                  {user ? (
                    <Link
                      to="/dashboard/profile"
                      className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                      title={t("nav.goToProfile")}
                    >
                      <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-semibold">
                        {userData?.firstName && userData?.lastName
                          ? `${userData.firstName.charAt(
                              0
                            )}${userData.lastName.charAt(0)}`
                          : user.email?.charAt(0).toUpperCase() || "U"}
                      </div>
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/dashboard/overview"
                        className="px-4 py-2 text-gray-700 hover:text-orange-600 transition-all font-medium"
                      >
                        {t("nav.dashboard")}
                      </Link>
                      <Link
                        to="/login"
                        className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all font-medium shadow-md hover:shadow-lg"
                      >
                        {t("nav.login")}
                      </Link>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/90 to-orange-400/85"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6 border border-white/30">
                <span className="text-sm font-semibold">
                  {t("home.titleHighlight")}
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                {t("home.title")}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                {t("home.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="px-8 py-4 bg-white text-orange-600 rounded-xl hover:bg-gray-50 transition-all font-bold text-lg text-center shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
                >
                  {t("home.accessDashboard")}
                </Link>
                <a
                  href="#about"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl hover:bg-white/20 transition-all font-bold text-lg text-center"
                >
                  {t("home.learnMore")}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <img
                  src="https://i.pinimg.com/736x/4d/2b/f9/4d2bf9d46fabcf33b0d5e62fece46644.jpg"
                  alt="Driving School Car"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-600/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                number: "7",
                label: t("home.stats.instructors"),
                icon: "👨‍🏫",
              },
              {
                number: "650",
                label: t("home.stats.trainingHours"),
                icon: "⏰",
              },
              {
                number: "400",
                label: t("home.stats.candidatesPerYear"),
                icon: "🎓",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all text-center border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-1"
              >
                <div className="text-5xl mb-4">{stat.icon}</div>
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-3">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-semibold text-lg">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://i.pinimg.com/1200x/94/5a/4e/945a4e41116e128ed9230a3fef565662.jpg"
                  alt="Driving School"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-600/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-orange-500 rounded-full opacity-20 blur-2xl"></div>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t("home.about.title")}
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>{t("home.about.description")}</p>
                <p>{t("home.about.description2")}</p>
                <p>{t("home.about.description3")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-24 bg-gradient-to-b from-white to-orange-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t("home.whyChoose.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("home.whyChoose.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: t("home.whyChoose.expertInstructors"),
                desc: t("home.whyChoose.expertInstructorsDesc"),
                icon: (
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ),
                image:
                  "https://i.pinimg.com/1200x/32/92/02/329202bea93922f4df56ecfc2b5791b5.jpg",
              },
              {
                title: t("home.whyChoose.safetyFirst"),
                desc: t("home.whyChoose.safetyFirstDesc"),
                icon: (
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                ),
                image:
                  "https://i.pinimg.com/1200x/2a/1b/01/2a1b013bf0140d8a64ebad67f0387194.jpg",
              },
              {
                title: t("home.whyChoose.flexibleSchedule"),
                desc: t("home.whyChoose.flexibleScheduleDesc"),
                icon: (
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
                image:
                  "https://i.pinimg.com/736x/27/8c/6b/278c6b7bc0b51ab408c6394281653eb0.jpg",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border-2 border-orange-100 hover:border-orange-300 transform hover:-translate-y-2 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-600/80 to-transparent"></div>
                  <div className="absolute top-4 left-4 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-xl">
                    {feature.icon}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t("home.services.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("home.services.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: t("home.services.training"),
                desc: t("home.services.trainingDesc"),
                icon: "🎓",
                image:
                  "https://i.pinimg.com/736x/69/e1/9d/69e19dcbedde2707e5efe8322c863150.jpg",
              },
              {
                title: t("home.services.instructorLecturer"),
                desc: t("home.services.instructorLecturerDesc"),
                icon: "👨‍🏫",
                image:
                  "https://i.pinimg.com/736x/05/54/12/055412d5e77f86327677a5fa95b33106.jpg",
              },
              {
                title: t("home.services.supplementaryHours"),
                desc: t("home.services.supplementaryHoursDesc"),
                icon: "⏰",
                image:
                  "https://i.pinimg.com/1200x/62/d3/4f/62d34f036e13dcf9d90c4a9d8de17063.jpg",
              },
              {
                title: t("home.services.categoryB"),
                desc: t("home.services.categoryBDesc"),
                icon: "🚗",
                image:
                  "https://images.unsplash.com/photo-1502877338535-766e1452684a?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden border-2 border-orange-100 hover:border-orange-400 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-2 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-600/70 to-transparent"></div>
                  <div className="absolute top-4 right-4 text-4xl">
                    {service.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1502877338535-766e1452684a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)",
            }}
          ></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t("home.cta.title")}
          </h2>
          <p className="text-xl text-white/90 mb-8">{t("home.cta.subtitle")}</p>
          <Link
            to="/login"
            className="inline-block px-10 py-5 bg-white text-orange-600 rounded-xl hover:bg-gray-50 transition-all font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
          >
            {t("home.cta.getStarted")}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="/logoWhite.png"
                  alt="Autoshkolla Burimi"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
                {t("home.footer.description")}
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/ashburimi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-orange-500/20 hover:bg-orange-500/30 flex items-center justify-center cursor-pointer transition-all"
                >
                  <svg
                    className="w-5 h-5 text-orange-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/autoshkollaburimi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-orange-500/20 hover:bg-orange-500/30 flex items-center justify-center cursor-pointer transition-all"
                >
                  <svg
                    className="w-5 h-5 text-orange-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.057-1.274-.07-1.649-.07-4.844 0-3.196.013-3.586.074-4.856.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-orange-400">
                {t("home.footer.quickLinks")}
              </h3>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <a
                    href="#about"
                    className="hover:text-orange-400 transition-all flex items-center gap-2"
                  >
                    <span className="text-orange-500">→</span>
                    {t("home.footer.aboutUs")}
                  </a>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="hover:text-orange-400 transition-all flex items-center gap-2"
                  >
                    <span className="text-orange-500">→</span>
                    {t("nav.login")}
                  </Link>
                </li>
                <li>
                  <a
                    href="#about"
                    className="hover:text-orange-400 transition-all flex items-center gap-2"
                  >
                    <span className="text-orange-500">→</span>
                    {t("home.footer.services")}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-orange-400">
                {t("home.footer.contact")}
              </h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">📍</span>
                  <span>{t("home.footer.address")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">📞</span>
                  <a
                    href={`tel:${t("home.footer.phone")}`}
                    className="hover:text-orange-400 transition-all"
                  >
                    {t("home.footer.phone")}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">📧</span>
                  <a
                    href={`mailto:${t("home.footer.email")}`}
                    className="hover:text-orange-400 transition-all"
                  >
                    {t("home.footer.email")}
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">🕐</span>
                  <span>{t("home.footer.schedule")}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              {t("home.footer.copyright")}
            </p>
            <p className="text-gray-400 text-sm">
              {t("home.footer.developedBy")}{" "}
              <a
                href="https://www.ardbraha.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-300 hover:underline font-medium transition-all"
              >
                Ard Braha
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
